import React, {useState, useEffect} from "react";
import {Button, Form, Input, Picker, Toast} from 'antd-mobile';
import { useNavigate, useLocation } from 'react-router-dom';
import logo from '../../../../icons/svgs/logo.svg';
import {EyeInvisibleOutline, EyeOutline} from "antd-mobile-icons";
import type {PickerValue} from "antd-mobile/es/components/picker";
import {login, loginWithVerificationCode, useHandleLoginSuccess} from "../../../../utils/auth";
import { t } from "../../../../i18n";
import { getAppVersion } from "utils/appRuntime";
import { isIOS, isAndroid } from "utils/deviceUtils";
import { NoAccountModal } from '../../../../components/NoAccountModal';
import { useSendVerificationCode } from '../../../../hooks/useSendVerificationCode';
import { validatePhoneNumber } from '../../../../utils/phoneValidation';
import { validateVerificationCodeFormat } from '../../../../utils/verificationCodeValidation';

const countryCodes = [
    { label: '+1', value: '+1', flag: '🇺🇸' },
];

type LoginType = 'password' | 'verificationCode';

const Login: React.FC = () => {
    const [form] = Form.useForm();
    const navigate = useNavigate();
    const location = useLocation();
    const handleLoginSuccess = useHandleLoginSuccess();
    const [loginType, setLoginType] = useState<LoginType>('password');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    // 验证码输入框的值
    const [verificationCode, setVerificationCode] = useState('');
    // 获取验证码倒计时（公共 hook 管理）
    const { sendCode: sendLoginVerificationCode, countdown } = useSendVerificationCode({
        type: 'login',
        getPhone: () => ({
            phone: form.getFieldValue('phoneOrEmail'),
            countryCode: form.getFieldValue('countryCode') || selectedCode
        }),
        onNotExist: () => setNoAccountModalVisible(true),
        onDisabled: () => Toast.show(t('errors.business.account.disabled')),
    });
    const [pickerVisible, setPickerVisible] = React.useState(false);
    const [selectedCode, setSelectedCode] = React.useState<string>('+1'); // 明确类型为 string
    const [isLoading, setIsLoading] = useState<boolean>(false);
    // 手机号不存在弹窗 Visible
    const [noAccountModalVisible, setNoAccountModalVisible] = useState(false);
    // 是否展示支持工具入口（仅test包展示）
    const [showSupportTools, setShowSupportTools] = useState(false);

    useEffect(() => {
        getAppVersion()
            .then((version) => {
                const lowerVersion = version.toLowerCase();
                if (['dev', 'test', 'stag', 'debug'].some((tag) => lowerVersion.includes(tag))) {
                    setShowSupportTools(true);
                }
            })
            .catch(() => {
                // 获取版本号失败时忽略，不展示支持工具入口
            });
    }, []);

    // 处理从忘记密码页面返回时的状态
    useEffect(() => {
        if (location.state) {
            const { phoneNumber, countryCode, loginType: stateLoginType } = location.state as any;
            if (phoneNumber) {
                form.setFieldsValue({
                    phoneOrEmail: phoneNumber,
                    countryCode: countryCode || '+1'
                });
                // 国家区号下拉框赋值
                setSelectedCode(countryCode || '+1');
            }
            if (stateLoginType) {
                setLoginType(stateLoginType);
            }
            // 清除 location.state，避免刷新时重复填充
            window.history.replaceState({}, document.title);
        }
    }, [location.state, form]);

    // 处理密码登录
    const onPasswordLogin = async (values: {countryCode: string; phoneOrEmail: string; password: string }) => {
        try {
            // 验证输入是否为空
            if (!values.phoneOrEmail || values.phoneOrEmail.trim() === '') {
                Toast.show(t('login.phone.mobileRequired'));
                return;
            }

      if (!values.password || values.password.trim() === "") {
        Toast.show(t("login.password.required"));
        return;
      }

            setIsLoading(true);
            const platform = isIOS() ? 'iOS' : isAndroid() ? 'Android' : 'Web';
            const versionName = await getAppVersion();
            const result = await login(Number(values.countryCode), values.phoneOrEmail, values.password, undefined, platform, versionName);

            if (result.userInfo) {
                handleLoginSuccess(result.userInfo);
                setTimeout(() => {
                    setIsLoading(false);
                }, 2000);
            } else {
                // 处理业务错误
                const messageKey = result.messageKey;
                if (messageKey === 'errors.business.account.blacklisted') {
                    // 黑名单账号，跳转到独立的SMS验证页面
                    setIsLoading(false);
                    navigate('/driver/blacklist-sms-verification', {
                        state: {
                            countryCode: values.countryCode,
                            phoneOrEmail: values.phoneOrEmail
                        }
                    });
                    return;
                } else if (messageKey === 'errors.business.account.notExist') {
                    setNoAccountModalVisible(true);
                    setIsLoading(false);
                } else if (
                    messageKey === 'errors.auth.incorrectAccountOrPassword' ||
                    messageKey === 'errors.auth.unauthorized'
                ) {
                    // 密码错误等：勿用 unauthorized 的「请登录后继续」；与原生端一致用统一登录失败文案
                    Toast.show(t('errors.login.failed'));
                    setIsLoading(false);
                } else if (messageKey === 'errors.business.account.locked') {
                    Toast.show(t('errors.business.account.locked'));
                    setIsLoading(false);
                } else if (result.messageKey) {
                    Toast.show(t(result.messageKey));
                    setIsLoading(false);
                } else {
                    Toast.show(t('errors.login.failed'));
                    setIsLoading(false);
                }
            }
        } catch (error: any) {
            // 处理真正的异常（网络错误等）
            Toast.show(t('errors.login.failed'));
            setIsLoading(false);
        }
    };


    // 处理验证码登录
    const onVerificationCodeLogin = async (values: {countryCode: string; phoneOrEmail: string }) => {
        try {
            // 1. 校验手机号
            const phoneValidation = validatePhoneNumber(values.phoneOrEmail, values.countryCode);
            if (!phoneValidation.isValid) {
                Toast.show(phoneValidation.errorMessage!);
                return;
            }

            // 2. 校验验证码格式
            const codeValidation = validateVerificationCodeFormat(verificationCode);
            if (!codeValidation.isValid) {
                Toast.show(codeValidation.errorMessage!);
                return;
            }

            // 3. 调用验证码登录API（后端会再次校验验证码）
            setIsLoading(true);
            const platform = isIOS() ? 'iOS' : isAndroid() ? 'Android' : 'Web';
            const versionName = await getAppVersion();
            const result = await loginWithVerificationCode(
                Number(values.countryCode.replace('+', '')),
                phoneValidation.phoneDigits!,
                verificationCode.trim(),
                undefined,
                true,
                platform,
                versionName
            );

            if (result.userInfo) {
                handleLoginSuccess(result.userInfo);
                setIsLoading(false);
            } else {
                // 处理业务错误
                const messageKey = result.messageKey;
                if (messageKey === 'errors.validation.invalidPhoneNumber') {
                    Toast.show(t('errors.validation.invalidPhoneNumber'));
                } else if (messageKey === 'errors.business.account.verificationCode.invalid') {
                    Toast.show(t('common.verificationCode.invalid'));
                } else if (messageKey === 'errors.business.account.verificationCode.notAllowedForRole') {
                    Toast.show(t('errors.business.account.verificationCode.notAllowedForRole'));
                } else if (messageKey === 'errors.business.account.notExist') {
                    // 账号不存在或离职司机，显示注册提示弹窗
                    setNoAccountModalVisible(true);
                } else if (messageKey === 'errors.business.account.disabled') {
                    Toast.show(t('errors.business.account.disabled'));
                } else {
                    Toast.show(t('errors.login.failed'));
                }
                setIsLoading(false);
            }
        } catch (error: any) {
            // 处理真正的异常（网络错误等）
            Toast.show(t('errors.login.failed'));
            setIsLoading(false);
        }
    };

    // 获取验证码
    const handleGetVerificationCode = async () => {
        await sendLoginVerificationCode();
    };

    const onFinish = async (values: any) => {
        if (loginType === 'password') {
            await onPasswordLogin(values);
        } else {
            await onVerificationCodeLogin(values);
        }
    };

    return (
        <div className="min-h-screen ">
            <div className="flex flex-col p-4 gap-8">
                {/* 右上角支持工具按钮（仅test包展示） */}
                {showSupportTools && (
                    <div className="flex justify-end">
                        <span
                            className="text-[var(--adm-color-primary)] text-sm cursor-pointer"
                            onClick={() => navigate('/support-tools')}
                        >
                            {t('supportTools.title')}
                        </span>
                    </div>
                )}

                {/* Logo */}
                <div className="text-center mb-8 mt-12">
                    <img
                        src={logo}
                        alt="SwiftX Logo"
                        className="w-[180px] min-w-[120px] h-auto mx-auto max-w-[60%]"
                    />
                </div>

                {/* 登录方式选择Tab */}
                <div className="flex gap-4 mb-6">
                    <div
                        className="flex-1 text-center py-2 cursor-pointer"
                        onClick={() => {
                            form.setFieldValue('phoneOrEmail', '');
                            setLoginType('password');
                            setVerificationCode('');
                        }}
                    >
                        <span
                            className={`inline-block font-semibold text-lg ${
                                loginType === 'password'
                                    ? 'text-[var(--adm-color-primary)] border-b-2 border-[var(--adm-color-primary)] pb-1'
                                    : 'text-[#888]'
                            }`}
                        >
                            {t('pages.login.password')}
                        </span>
                    </div>
                    <div
                        className="flex-1 text-center py-2 cursor-pointer"
                        onClick={() => {
                            // 切换到验证码登录时，直接清空
                            form.setFieldValue('phoneOrEmail', '');
                            setLoginType('verificationCode');
                            setPassword('');
                            setShowPassword(false);
                        }}
                    >
                        <span
                            className={`inline-block font-semibold text-lg ${
                                loginType === 'verificationCode'
                                    ? 'text-[var(--adm-color-primary)] border-b-2 border-[var(--adm-color-primary)] pb-1'
                                    : 'text-[#888]'
                            }`}
                        >
                            {t('pages.login.verificationCode')}
                        </span>
                    </div>
                </div>
                {/*注册&&用户协议&&隐私政策*/}
                <Form
                    form={form}
                    onFinish={onFinish}
                    initialValues={{ countryCode: '+1' }} // 设置表单初始值
                    layout="vertical"
                    style={{
                        '--border-top': 'none',
                        '--border-inner': 'none',
                        '--border-bottom': 'none',
                    }}
                    footer={
                        <div>
                            <div className="text-center">
                                <span
                                    className="text-[#6C63FF] font-semibold text-sm sm:text-base cursor-pointer"
                                    onClick={() => navigate('/driver/register')}
                                >
                                    {t(`pages.login.registerDriver`)}
                                </span>
                            </div>

                            <div className="text-center text-[#888]">
                                <div className="font-semibold">
                                    {t(`pages.login.registerDesc1`)}, {t(`pages.login.registerDesc2`)}
                                </div>
                                <div className="font-semibold">
                                    <span
                                        className="text-[#6C63FF] font-semibold ml-1"
                                        onClick={() => navigate('/driver/terms-of-use')}
                                    >
                                        {t(`pages.login.termsOfUse`)}
                                    </span>
                                    <span> {t(`pages.login.and`)} </span>
                                    <span
                                        className="text-[#6C63FF] font-semibold"
                                        onClick={() => navigate('/driver/privacy-policy')}
                                    >
                                        {t(`pages.login.privacyPolicy`)}
                                    </span>
                                </div>
                            </div>
                        </div>
                    }
                >
                    {/*区号&&电话号*/}
                    <div className="flex gap-2 mb-4">
                        <Form.Item name="countryCode" className="mb-0 w-[70px]">
                            <div
                                className="w-full h-full flex items-center justify-center gap-1 cursor-pointer"
                                onClick={() => setPickerVisible(true)}
                            >
                                <span className="text-lg">
                                    {countryCodes.find(c => c.value === selectedCode)?.flag || '🇺🇸'}
                                </span>
                                <span>{selectedCode}</span>
                            </div>
                        </Form.Item>

                        <Form.Item name="phoneOrEmail" className="mb-0 flex-1">
                            <Input
                                clearable
                                placeholder={t('pages.login.phoneNumber')}
                                type={loginType === 'password' ? 'text' : 'tel'}
                                inputMode="tel"
                                value={form.getFieldValue('phoneOrEmail') || ''}
                                onChange={val => {
                                    if (loginType === 'password') {
                                        // 密码登录：不限制输入，允许任何字符
                                        form.setFieldValue('phoneOrEmail', val);
                                    } else {
                                        // 验证码登录：只允许数字，限制长度（中国11位，美国10位）
                                        const digitsOnly = val.replace(/\D/g, '');
                                        const maxLength = selectedCode === '+86' ? 11 : 10;
                                        form.setFieldValue('phoneOrEmail', digitsOnly.slice(0, maxLength));
                                    }
                                }}
                            />
                        </Form.Item>
                    </div>

                    <Picker
                        columns={[countryCodes.map(code => ({
                            label: `${code.flag} ${code.label}`,
                            value: code.value
                        }))]}
                        visible={pickerVisible}
                        value={[selectedCode]}
                        onClose={() => setPickerVisible(false)}
                        onConfirm={(val: PickerValue[]) => {  // 使用 PickerValue[] 类型
                            const newCode = String(val[0]);  // newCode 会自动推断为 PickerValue 类型
                            setSelectedCode(newCode);
                            form.setFieldsValue({ countryCode: newCode });
                            setPickerVisible(false);
                        }}
                    />
                    {/*输入密码/验证码*/}
                    {loginType === 'password' ? (
                        <>
                            <Form.Item
                                name="password"
                                className="w-full"
                            >
                                <div className="adm-input">
                                    <Input
                                        className="adm-input-element"
                                        placeholder={t('pages.login.password')}
                                        type={showPassword ? 'text' : 'password'}
                                        value={password}
                                        onChange={val => setPassword(val)}
                                    />
                                    <div
                                        className="cursor-pointer px-2"
                                        onClick={() => setShowPassword(!showPassword)}
                                    >
                                        {showPassword ? <EyeOutline /> : <EyeInvisibleOutline />}
                                    </div>
                                </div>
                            </Form.Item>
                            <div className="text-right mb-4">
                                <span
                                    className="text-[var(--adm-color-primary)] text-sm cursor-pointer"
                                    onClick={() => {
                                        navigate('/driver/changePassword');
                                    }}
                                >
                                    {t('pages.login.forgotPassword')}
                                </span>
                            </div>
                        </>
                    ) : (
                        <>
                            <Form.Item className="w-full">
                                <div className="adm-input">
                                    <Input
                                        className="adm-input-element"
                                        placeholder={t('common.verificationCode.placeholder')}
                                        type="tel"
                                        inputMode="numeric"
                                        value={verificationCode}
                                        onChange={val => {
                                            const digitsOnly = val.replace(/\D/g, '').slice(0, 6);
                                            setVerificationCode(digitsOnly);
                                        }}
                                    />
                                    <div className="px-2 whitespace-nowrap flex-shrink-0">
                                        <Button
                                            size="small"
                                            color="primary"
                                            fill="none"
                                            disabled={countdown > 0}
                                            onClick={handleGetVerificationCode}
                                        >
                                            {countdown > 0 ? `${countdown}s` : t('pages.login.getCode')}
                                        </Button>
                                    </div>
                                </div>
                            </Form.Item>
                            <div className="text-right mb-4">

                            </div>
                        </>
                    )}

                    <Form.Item>
                        <div className="flex justify-center">
                            <Button
                                block
                                color="primary"
                                size="large"
                                type="submit"
                                loading={isLoading}
                            >
                                {t('pages.login.logIn')}
                            </Button>
                        </div>
                    </Form.Item>
                </Form>

                {/* 手机号不存在弹窗提醒 */}
                <NoAccountModal
                    visible={noAccountModalVisible}
                    onClose={() => setNoAccountModalVisible(false)}
                />
            </div>
        </div>
    );
};

export default Login;
